import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import { useTheme } from "../hooks/useTheme";
import styles from "./ParentSidebar.module.css";
import { setPreviewMode } from "./PreviewBanner";

interface Props { pendingCount?: number; }

export default function ParentSidebar({ pendingCount = 0 }: Props) {
  const navigate = useNavigate();
  const clearAuth  = useAuthStore((s) => s.clearAuth);
  const parentRole = useAuthStore((s) => s.parentRole);
  const email      = useAuthStore((s) => s.email);
  const family     = useFamilyStore((s) => s.family);
  const members    = useFamilyStore((s) => s.members);
  const me         = members.find((m) => m.email === email);
  const theme      = useTheme();

  const roleLabel = parentRole === "admin" ? "Admin" : parentRole === "manager" ? "Manager" : parentRole === "viewer" ? "Viewer" : "Parent";

  // Tablet collapsed state — persisted
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1024 && window.innerWidth >= 768;
  });

  useEffect(() => {
    function onResize() {
      if (window.innerWidth >= 1024) setCollapsed(false);
      else if (window.innerWidth >= 768) setCollapsed(true);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function logout() { clearAuth(); navigate("/"); }

  const cls = `${styles.sidebar} ${collapsed ? styles.collapsed : ""}`;

  return (
    <nav className={cls}>
      {/* Toggle button — only visible on tablet */}
      <button className={styles.toggleBtn} onClick={() => setCollapsed((v) => !v)} title={collapsed ? "Expand" : "Collapse"}>
        {collapsed ? "›" : "‹"}
      </button>

      <div className={styles.brand}>
        <span className={styles.brandIcon}>⭐</span>
        <span className={styles.brandText}>StarQuest <span className={styles.pill}>{roleLabel.toUpperCase()}</span></span>
      </div>

      <div className={styles.section}>Manage</div>
      <div className={styles.grid}>
        <NavLink to="/parent" end className={tile} title="Home">
          <span className={styles.tileIcon}>🏠</span>
          <span className={styles.tileLabel}>Home</span>
        </NavLink>
        <NavLink to="/parent/builder" className={tile} title="Builder">
          <span className={styles.tileIcon}>🏗️</span>
          <span className={styles.tileLabel}>Builder</span>
        </NavLink>
        <NavLink to="/parent/add-challenge" className={tile} title={`Create ${theme.challengeName}`}>
          <span className={styles.tileIcon}>🌟</span>
          <span className={styles.tileLabel}>Create {theme.challengeName}</span>
        </NavLink>
        <NavLink to="/parent/challenges" className={tile} title={theme.challengeNamePlural}>
          <span className={styles.tileIcon}>✅</span>
          <span className={styles.tileLabel}>{theme.challengeNamePlural}</span>
          {pendingCount > 0 && <span className={styles.badge}>{pendingCount}</span>}
        </NavLink>
        <NavLink to="/parent/store" className={tile} title="Store">
          <span className={styles.tileIcon}>🏪</span>
          <span className={styles.tileLabel}>Store</span>
        </NavLink>
        <NavLink to="/parent/tasks" className={tile} title="Tasks">
          <span className={styles.tileIcon}>📋</span>
          <span className={styles.tileLabel}>Tasks</span>
        </NavLink>
      </div>

      <div className={styles.section}>Family</div>
      <div className={styles.grid}>
        <NavLink to="/parent/family-hub" className={tile} title="Family Hub">
          <span className={styles.tileIcon}>👨‍👩‍👧</span>
          <span className={styles.tileLabel}>Family Hub</span>
        </NavLink>
        <NavLink to="/parent/points-bank" className={tile} title={theme.pointBankTitle}>
          <span className={styles.tileIcon}>🏦</span>
          <span className={styles.tileLabel}>{theme.pointBankTitle}</span>
        </NavLink>
        <NavLink to="/parent/gift-points" className={tile} title={`Gift ${theme.pointNamePlural}`}>
          <span className={styles.tileIcon}>🎁</span>
          <span className={styles.tileLabel}>Gift {theme.pointNamePlural}</span>
        </NavLink>
        <NavLink to="/parent/points-value" className={tile} title={`${theme.pointNamePlural} Value`}>
          <span className={styles.tileIcon}>💵</span>
          <span className={styles.tileLabel}>{theme.pointNamePlural} Value</span>
        </NavLink>
      </div>

      <button type="button" className={styles.previewBtn} title={`View as ${theme.kidRole}`}
        onClick={() => { setPreviewMode(); navigate("/kid"); }}>
        <span>👁</span>
        <span className={styles.previewLabel}>View as {theme.kidRole}</span>
      </button>

      <div className={styles.foot}>
        <div className={styles.footTop}>
          <div className={styles.avi}>{me?.avatar ?? "👤"}</div>
          <div className={styles.footText}>
            <div className={styles.name}>{me?.displayName ?? email ?? "Me"}</div>
            <div className={styles.role}>{family?.name ?? "My Family"}</div>
            <div className={styles.role}>{roleLabel}</div>
          </div>
        </div>
        <button className={styles.logout} onClick={logout} title="Sign out">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          <span className={styles.logoutLabel}>Sign Out</span>
        </button>
      </div>
    </nav>
  );
}

const tile = ({ isActive }: { isActive: boolean }) =>
  `${styles.tile} ${isActive ? styles.tileActive : ""}`;
